import React from "react";
import Button from "~/components/ui/Button"
import { formatPrice } from "@decocms/apps-commerce/sdk/formatPrice"
import { useSignal } from "~/sdk/signal"
import { useCart } from "~/hooks/useCart"
import type { SKU } from "@decocms/apps-vtex/utils/types"
import Icon from "./Icon"

export interface Props {
    items: Array<SKU>
}

const formatShippingEstimate = (estimate: string) => {
    const [, time, type] = estimate.split(/(\d+)/)

    if (type === 'bd') return `${time} dias úteis`
    if (type === 'd') return `${time} dias`
    if (type === 'h') return `${time} horas`
}

function ShippingContent({
    shippings,
    postalCode,
}: {
    shippings: Map<string, { price: number; shippingEstimate: string }>
    postalCode: string
}) {
    const { cart } = useCart()

    if (shippings.size === 0 || !cart?.value) {
        return null
    }

    const locale = cart?.value?.clientPreferencesData?.locale || 'pt-BR'
    const currencyCode = cart?.value?.storePreferencesData?.currencyCode || 'BRL'

    postalCode = postalCode.replace(/(\d{5})(\d{3})/, '$1-$2')

    return (
        <table className='mt-10 text-left text-sm [&_th]:border [&_th]:leading-[100%] [&_th]:p-[10px] [&_td]:leading-[100%] [&_td]:border [&_td]:p-[10px] border-[#dbdbdb] border-collapse border-spacing-0 text-black'>
            <thead>
                <tr>
                    <th>Valor do frete deste produto</th>
                    <th>Disponibilidade</th>
                </tr>
            </thead>
            <tbody>
                {[...shippings.entries()]
                    .sort(([_oldId, oldA], [_newId, newB]) => oldA.price - newB.price)
                    .map(([id, { price, shippingEstimate }]) => (
                        <tr>
                            <td>
                                {price === 0
                                    ? 'Grátis'
                                    : formatPrice(price / 100, currencyCode, locale)}
                            </td>
                            <td>
                                Frete {id}, entrega em {formatShippingEstimate(shippingEstimate)}
                                {' '}
                                para o CEP {postalCode}
                            </td>
                        </tr>
                    ))}
            </tbody>
        </table>
    )
}

function useInputSignal(initialValue: string) {
    const raw = useSignal(initialValue);
    const masked = useSignal(initialValue);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.currentTarget.value.replace(/\D/g, '');
        masked.value = val.replace(/(\d{5})(\d)/g, '$1-$2');
        raw.value = val;
    };

    return { raw, masked, handleInput };
}

function ShippingSimulation({ items }: Props) {
    const { raw, masked, handleInput } = useInputSignal('');
    const loading = useSignal(false)
    const shippings = useSignal<Map<string, { price: number; shippingEstimate: string }>>(new Map())
    const { simulate, cart } = useCart()

    const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (raw.value.length !== 8) {
            return
        }

        try {
            loading.value = true
            const simulateResult = await simulate({
                items: items.map((sku) => ({ ...sku, id: String(sku.id) })),
                postalCode: raw.value,
                country: cart?.value?.storePreferencesData?.countryCode || 'BRA',
            })

            const allSlas = simulateResult.logisticsInfo.flatMap(({ slas }) => slas)

            // VTEX api doesn't return the total price of product and gift together, so we need to calculate it
            for (const sla of allSlas) {
                if (shippings.value.has(sla.id)) continue

                const dupSlas = allSlas.filter((s) =>
                    s.id === sla.id && s.deliveryChannel === sla.deliveryChannel
                )

                shippings.value.set(sla.id, {
                    price: dupSlas.reduce((acc, sla) => acc + sla.price, 0),
                    shippingEstimate: dupSlas[0].shippingEstimate,
                })
            }

            shippings.value = new Map(shippings.value)
        } finally {
            loading.value = false
        }
    }

    return (
        <div>
            <div className='flex items-center gap-2 text-sm'>
                <Icon id='TruckPDP' width={21} height={16} />
                <span>Consulte o prazo de entrega</span>
            </div>

            <form className='join w-full mt-2' onSubmit={handleFormSubmit}>
                <input
                    aria-label='Digite seu CEP'
                    type='text'
                    name='postalCode'
                    className='input input-bordered focus-visible:outline-none join-item w-full text-sm h-[42px]'
                    placeholder='Digite seu CEP'
                    pattern='\d{5}-\d{3}'
                    value={masked.value}
                    maxLength={9}
                    size={8}
                    onChange={handleInput}
                />
                <Button
                    type='submit'
                    loading={loading.value}
                    className='join-item btn-square min-h-0 h-[42px] w-[42px]'
                >
                    OK
                </Button>
            </form>
            <ShippingContent shippings={shippings.value} postalCode={raw.value} />
            <div className='mt-4 min-[1025px]:mt-8'>
                <a
                    href={'https://buscacepinter.correios.com.br/app/endereco/index.php?t'}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-black underline text-xs'
                >
                    Não sei meu CEP
                </a>
            </div>
        </div>
    )
}

export default ShippingSimulation
